import React from 'react';
import { Button } from '../../../components/ui';
import { EyeIcon, TrashIcon, UploadCloudIcon } from '../../../components/ui/Icons';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  deleteManagementCompanyLogo,
  uploadManagementCompanyLogo,
  useManagementCompanyLogoBlobUrl,
} from './useData';

type Props = {
  companyId: number;
  logoStorageKey?: string | null;
  logoOriginalFilename?: string | null;
  onChanged?: () => void;
  /** Compact thumbnail for list rows */
  compact?: boolean;
  editable?: boolean;
};

/** Logos often use white artwork + transparency; always preview on dark so they stay visible. */
const LOGO_STAGE =
  'flex items-center justify-center overflow-hidden rounded-lg border border-neutral-700 bg-neutral-950';

export const ManagementCompanyLogoPanel: React.FC<Props> = ({
  companyId,
  logoStorageKey,
  logoOriginalFilename,
  onChanged,
  compact = false,
  editable = true,
}) => {
  const { colors } = useTheme();
  const { logoBlobUrl, loading } = useManagementCompanyLogoBlobUrl(companyId, logoStorageKey);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [viewOpen, setViewOpen] = React.useState(false);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadManagementCompanyLogo(companyId, file);
      onChanged?.();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed';
      setError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteManagementCompanyLogo(companyId);
      onChanged?.();
    } catch {
      setError('Could not remove logo');
    } finally {
      setBusy(false);
    }
  };

  const openView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (logoBlobUrl) setViewOpen(true);
  };

  const viewModal = (
    <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} size="lg">
      <ModalHeader>
        <ModalTitle>{logoOriginalFilename || 'Management company logo'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className={cn(LOGO_STAGE, 'min-h-[240px] w-full p-6')}>
          {logoBlobUrl ? (
            <img
              src={logoBlobUrl}
              alt={logoOriginalFilename || 'Logo'}
              className="max-h-[70vh] max-w-full object-contain"
            />
          ) : null}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="secondary" size="sm" onClick={() => setViewOpen(false)}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );

  if (compact) {
    return (
      <>
        <button
          type="button"
          title={logoBlobUrl ? 'View logo' : undefined}
          disabled={!logoBlobUrl}
          onClick={openView}
          className={cn(
            LOGO_STAGE,
            'h-8 w-14 shrink-0',
            logoBlobUrl && 'cursor-pointer hover:ring-2 hover:ring-indigo-400/40',
          )}
        >
          {logoBlobUrl ? (
            <img src={logoBlobUrl} alt="" className="h-full w-full object-contain p-0.5" />
          ) : (
            <span className="text-[10px] text-neutral-500">—</span>
          )}
        </button>
        {viewModal}
      </>
    );
  }

  return (
    <>
      <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
        <div className="mb-2 text-sm font-semibold">Logo</div>
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            title={logoBlobUrl ? 'View logo' : undefined}
            disabled={!logoBlobUrl}
            onClick={openView}
            className={cn(
              LOGO_STAGE,
              'h-24 w-48 shrink-0',
              logoBlobUrl && 'cursor-pointer hover:ring-2 hover:ring-indigo-400/40',
            )}
          >
            {loading ? (
              <span className="text-xs text-neutral-400">…</span>
            ) : logoBlobUrl ? (
              <img
                src={logoBlobUrl}
                alt={logoOriginalFilename || 'Logo'}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <span className="px-2 text-center text-xs text-neutral-500">No logo</span>
            )}
          </button>
          <div className="min-w-0 flex-1 space-y-2">
            <p className={cn('text-xs', colors.textMuted)}>
              PNG, JPG, GIF, WebP, BMP, or SVG · max 5 MB
              {logoOriginalFilename ? ` · ${logoOriginalFilename}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {logoBlobUrl ? (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8"
                  title="View logo"
                  aria-label="View logo"
                  onClick={openView}
                >
                  <EyeIcon className="h-4 w-4" />
                </Button>
              ) : null}
              {editable ? (
                <>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/bmp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
                    className="hidden"
                    onChange={(e) => void onPick(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    size="icon"
                    className="h-8 w-8"
                    disabled={busy}
                    title={logoStorageKey ? 'Replace logo' : 'Upload logo'}
                    aria-label={logoStorageKey ? 'Replace logo' : 'Upload logo'}
                    onClick={() => inputRef.current?.click()}
                  >
                    <UploadCloudIcon className="h-4 w-4" />
                  </Button>
                  {logoStorageKey ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="danger"
                      className="h-8 w-8"
                      disabled={busy}
                      title="Remove logo"
                      aria-label="Remove logo"
                      onClick={() => void onRemove()}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
            {error ? <p className="text-xs text-red-600">{error}</p> : null}
          </div>
        </div>
      </div>
      {viewModal}
    </>
  );
};

export default ManagementCompanyLogoPanel;
